import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Crown, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { StatusBadge } from "../components/StatusBadge";
import { CreditsPanel } from "../components/billing/Credits";
import { PricingGuide } from "../components/PricingGuide";

export const LOCKED = "Feature locked. Upgrade to Premium for active AI engineering and automated optimization.";

export function PremiumGate({ children }) {
  const { user } = useAuth();
  if (user?.entitlements?.all_premium_features) return children;
  return (
    <div className="panel p-8 border-amber-500/30 text-center" data-testid="premium-locked">
      <Lock className="mx-auto text-amber-400 mb-3" />
      <p className="text-slate-200 mb-4">{LOCKED}</p>
      <Link to="/app/billing" className="text-amber-400 underline" data-testid="upgrade-link">See Premium plans</Link>
    </div>
  );
}

export default function BillingPage() {
  const [b, setB] = useState(null);
  const load = () => api.get("/billing/me").then((r) => setB(r.data));
  useEffect(() => { load(); }, []);
  if (!b) return <p className="text-slate-500">Loading…</p>;
  const buy = async (period) => {
    try { const { data } = await api.post("/payments/checkout", { period, origin_url: window.location.origin }); window.location.href = data.checkout_url; } catch (e) { toast.error(errMsg(e)); }
  };
  const cancel = async () => { if (!window.confirm("Cancel at the end of the current period?")) return; try { await api.post("/billing/cancel"); toast.success("Cancellation scheduled"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="max-w-4xl">
      <p className="eyebrow mb-2">{b.role === "owner" ? "Master profile" : "Your account"}</p>
      <h1 className="font-display text-4xl sm:text-5xl mb-8">Plan & Billing</h1>
      <div className="panel p-6 mb-6 flex flex-wrap gap-6 justify-between" data-testid="plan-summary">
        <div><p className="text-xs text-slate-500">Current plan</p><p className="text-2xl font-semibold" data-testid="current-plan">{b.plan === "owner_premium" ? "Master Owner · Premium ($0)" : b.premium ? "Premium" : "Free (self-serve)"}</p>
          {b.subscription && <p className="text-xs text-slate-500 mt-1">Subscription {b.subscription.status}{b.subscription.cancel_at_period_end ? " · cancels at period end" : ""} · {fmtDate(b.subscription.updated_at)}</p>}</div>
        <div className="text-sm text-slate-400"><p>{b.books} books · {b.ai_requests} AI requests</p><p className="text-xs">Payments mode: <span className="font-mono">{b.stripe_mode}</span></p></div>
      </div>
      <CreditsPanel />
      <div data-testid="billing-pricing-guide"><p className="eyebrow mb-3">Typical AI costs by format</p><PricingGuide compact /></div>
      {b.role !== "owner" && (
        <div className="grid md:grid-cols-2 gap-5">
          <div className="panel p-6" data-testid="plan-free"><p className="font-display text-2xl mb-2">Free</p><p className="text-sm text-slate-400 mb-4">Manual editor, templates, structure blueprints, layout preview and exports. AI features use credits — new authors get welcome credits, and packs top them up.</p><StatusBadge status={b.premium ? "pending" : "PASS"} label={b.premium ? "available" : "your plan"} /></div>
          <div className="panel p-6 border-amber-500/40" data-testid="plan-premium"><p className="font-display text-2xl mb-2 flex items-center gap-2"><Crown className="text-amber-400" size={20} />Premium</p>
            <p className="text-sm text-slate-400 mb-4">Full AI suite plus AI credits included every month: writing assistant, editing, rewrite & expansion, continuity checks, narration, cover concepts.</p>
            {b.premium ? (b.subscription?.id && !b.subscription.cancel_at_period_end && <Button variant="outline" className="border-slate-700" onClick={cancel} data-testid="cancel-subscription">Cancel subscription</Button>) : !b.payments_enabled ? <p className="text-xs text-slate-500">Subscriptions are not open yet.</p> : (
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => buy("monthly")} className="bg-amber-600 hover:bg-amber-500" data-testid="buy-monthly">${b.prices.monthly}/month</Button>
                <Button onClick={() => buy("yearly")} variant="outline" className="border-amber-500/50 text-amber-300" data-testid="buy-yearly">${b.prices.yearly}/year</Button>
              </div>)}
          </div>
        </div>
      )}
    </div>
  );
}

export function PaymentResult({ ok }) {
  const [params] = useSearchParams();
  const { check } = useAuth();
  const [status, setStatus] = useState(ok ? "checking" : "cancelled");
  const [kind, setKind] = useState("subscription");
  useEffect(() => {
    if (!ok) return;
    const sid = params.get("session_id");
    let n = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setKind(data.kind); setStatus("paid"); check(); return; }
      } catch { setStatus("error"); return; }
      if (++n < 15) setTimeout(poll, 2000); else setStatus("timeout");
    };
    poll();
  }, [ok]); // eslint-disable-line
  const msg = { checking: "Confirming your payment…", paid: kind === "credits" ? "Payment confirmed — your credits have been added." : "Payment confirmed — Premium is active.", cancelled: "Checkout cancelled. No charge was made.", error: "We couldn't find this payment.", timeout: "Still processing. Refresh in a minute." }[status];
  return (
    <div className="min-h-screen grid place-items-center p-6">
      <div className="panel p-10 text-center max-w-md" data-testid="payment-result"><p className="font-display text-2xl mb-4" data-testid="payment-status">{msg}</p><Link to="/app/billing" className="text-amber-400" data-testid="payment-back">Back to Plan & Billing</Link></div>
    </div>
  );
}
